/* renderTemplates.js
 *
 * Centralized theme → CSS variable plumbing and shared Select props.
 * All templates use:
 *  - buildStyleVars(theme): maps theme.selectTokens into CSS vars (single source of truth)
 *  - getPadX(theme, variant): consistent left/right padding for visible value
 *  - getSelectCommon(variant, theme): common MUI props (size, input, MenuProps, sx padding)
 *  - makeRootClass(variant, editable): consistent root class for grid/panel + readonly
 *
 * No functionality removed; tree, pill, flags, boolean, readonly rendering unchanged.
 */


import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon   from "@mui/icons-material/ExpandMoreRounded";
import FiberManualRecordIcon   from "@mui/icons-material/FiberManualRecord";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon  from "@mui/icons-material/KeyboardArrowDownRounded";

import React from "react";
import {
  Select,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Chip,
  Typography,
  Popover,
  OutlinedInput,
  InputAdornment,
  IconButton,
  ListItemIcon,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import "./renderTemplates.css";
import "./renderTemplates.enhanced.css";


/* ------------------------- icon registry ------------------------- */

const iconMap = {
  Cancel: <CancelIcon fontSize="small" />,
  CheckCircle: <CheckCircleIcon fontSize="small" />,
  RemoveCircle: <RemoveCircleIcon fontSize="small" />,
  AddCircleOutline: <AddCircleOutlineIcon fontSize="small" />,
};

/* ------------------------- shared helpers ------------------------- */

const warnedThemePaths = new Set();

function warnMissingThemePath(path) {
  if (warnedThemePaths.has(path)) return;
  warnedThemePaths.add(path);
  console.warn(`[theme] Missing required theme value: ${path}`);
}

function getThemePath(source, path) {
  const value = path.split(".").reduce((acc, key) => acc?.[key], source);
  if (value === undefined || value === null || value === "") {
    warnMissingThemePath(path);
    return undefined;
  }
  return value;
}

function cssPx(value) {
  return value === undefined ? undefined : `${value}px`;
}

/** Map theme.selectTokens → CSS variables once. */
function buildStyleVars(theme) {
  const grid    = getThemePath(theme, "selectTokens.metrics.grid");
  const panel   = getThemePath(theme, "selectTokens.metrics.panel");
  const pill    = getThemePath(theme, "selectTokens.pill");
  const flags   = getThemePath(theme, "selectTokens.flags");
  const borders = getThemePath(theme, "selectTokens.borders.panel");
  const tree    = getThemePath(theme, "selectTokens.tree");
  const tcol    = getThemePath(theme, "selectTokens.tree.colours");
  const menu    = getThemePath(theme, "selectTokens.menu");
  const typo    = getThemePath(theme, "selectTokens.typography");

  return {
    // brand colors
    "--rt-main": getThemePath(theme, "mainColour"),
    "--rt-secondary": getThemePath(theme, "secondaryColour"),

    // typography
    "--rt-font-family": typo.fontFamily,
    "--rt-text-color":  typo.textColour,
    "--rt-font-size-grid": typo.gridFontSize,
    "--rt-font-size-dp":   typo.panelFontSize,
    "--rt-placeholder": typo.placeholderColour,
    "--rt-empty":       typo.emptyColour,

    // grid metrics
    "--rt-padY-grid": cssPx(grid.padY),
    "--rt-padX":      cssPx(grid.padX),
    "--rt-ro-padX":   cssPx(grid.padX),
    "--rt-ro-padY-grid": cssPx(grid.padY),
    "--rt-radius-grid": cssPx(grid.radius),

    // panel metrics + borders
    "--rt-padY-dp": cssPx(panel.padY),
    "--rt-ro-padY-dp": cssPx(panel.padY),
    "--dp-border-radius": cssPx(panel.radius),
    "--dp-max-width": cssPx(panel.maxWidth),
    "--dp-border-color": borders.default,
    "--dp-border-hover": borders.hover,
    "--dp-border-focus": borders.focus,
    "--dp-border-w": cssPx(borders.width),
    "--dp-border-w-hover": cssPx(borders.activeWidth),

    // pill
    "--rt-chip-font": pill.chipFontSize,
    "--rt-chip-padY": cssPx(pill.chipPadY),
    "--rt-chip-padX": cssPx(pill.chipPadX),
    "--rt-chip-radius": cssPx(pill.chipRadius),
    "--rt-chip-minw": cssPx(pill.minChipWidth),
    "--rt-chip-fixedw": cssPx(pill.minChipWidth),

    // flags
    "--rt-flag-w": cssPx(flags.width),
    "--rt-flag-h": cssPx(flags.height),
    "--rt-flag-r": cssPx(flags.radius),

    // menu / popover sizing
    "--rt-popover-minw": cssPx(tree.popoverMinWidth),
    "--rt-popover-maxh": cssPx(tree.popoverMaxHeight),
    "--rt-tree-item-padY": cssPx(tree.itemPadY),

    // checkbox & readonly bg
    "--rt-checkbox-checked": getThemePath(theme, "selectTokens.checkbox.checkedColour"),
    "--rt-ro-panel-bg": getThemePath(theme, "selectTokens.readonly.panelBackground"),

    // tree full state theming
    "--rt-tree-bg":           tcol.bg,
    "--rt-tree-fg":           tcol.fg,
    "--rt-tree-hover-bg":     tcol.hoverBg,
    "--rt-tree-hover-fg":     tcol.hoverFg,
    "--rt-tree-selected-bg":  tcol.selectedBg,
    "--rt-tree-selected-fg":  tcol.selectedFg,
    "--rt-tree-focused":      tcol.focusedRing,
    "--rt-tree-highlight-bg": tcol.highlightBg,
    "--rt-tree-highlight-fg": tcol.highlightFg,
    "--rt-tree-branch-fg":    tcol.branchFg,
    "--rt-tree-desc-fg":      tcol.descFg,

    // expose menu min width for portals (used via MenuProps.sx)
    __menuMinWidth: menu.minWidth,
  };
}

/** Return left/right padding px for visible select value for variant. */
function getPadX(theme, variant) {
  if (variant === "grid") return getThemePath(theme, "selectTokens.metrics.grid.padX");
  return getThemePath(theme, "selectTokens.metrics.panel.padX");
}

/** Build common props for MUI Select (size, input for grid, MenuProps, sx padding). */
function getSelectCommon(variant, theme) {
  const isGrid = variant === "grid";
  const size   = isGrid ? "small" : "medium";
  const padX   = getPadX(theme, variant);
  const menuMinW = getThemePath(theme, "selectTokens.menu.minWidth");

  // Reserve space for the Select icon so labels never run underneath it
  const caretSpace = isGrid ? 28 : 36;
  const selectPaddingLeft = padX === undefined ? undefined : `${padX}px`;
  const selectPaddingRight = padX === undefined ? undefined : `${padX + caretSpace}px`;
  const minWidth = menuMinW === undefined ? undefined : `${menuMinW}px`;

  return {
    size,
    fullWidth: true,
    input: isGrid ? <OutlinedInput className="rt-flat-input" /> : undefined,
    sx: {
      "& .MuiSelect-select": {
        paddingLeft: selectPaddingLeft,
        paddingRight: selectPaddingRight,
        minWidth: 0,
      },
    },
    MenuProps: {
      disableScrollLock: true,
      PaperProps: { sx: { minWidth } },
    },
  };
}

/** Make root class per variant + editable. */
function makeRootClass(variant, editable) {
  const isGrid = variant === "grid";
  const ro = !editable ? "rt-readonly" : "";
  return `rt-cell rt-align ${isGrid ? "" : "dp-select-root"} ${ro}`;
}

/** Chip fixed width used to line up icons/labels in trigger and menu. */
function getChipFixedWidth(theme) {
  return getThemePath(theme, "selectTokens.pill.minChipWidth");
}

/* ========================== DefaultSelect ========================= */

export function DefaultSelect({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  options,
  theme,
  variant,       // "grid" | "detail"
  allowNull = true,
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls   = makeRootClass(variant, editable);
  const selectCls = `rt-select ${isGrid ? "rt-flat-select" : "dp-select"}`;
  const common    = getSelectCommon(variant, theme);

  const labelFor = (val) => {
    const opt = safeOptions.find((o) => o?.value === val);
    return opt?.name ?? (val ?? "");
  };

  if (!editable) {
    const displayText = labelFor(value);

    return (
      <Box className={rootCls} style={styleVars}>
        {displayText || <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  const safeValue = normalizedValue ?? "";

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        className={selectCls}
        multiple={false}
        value={safeValue}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          const label = labelFor(selected);
          if (!label) return <Typography variant="body2" className="rt-empty" />;

          return (
            <Tooltip title={label} placement="top" arrow>
              <span className="rt-select-ellipsis">{label}</span>
            </Tooltip>
          );
        }}
      >
        {allowNull && <MenuItem value=""><em>None</em></MenuItem>}
        {safeOptions.map((opt, index) => (
          <MenuItem key={opt?.value ?? index} value={opt?.value} className="rt-menuitem">
            {opt?.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* ========================== MultiSelect ========================= */

export function MultiSelect({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  options,
  theme,
  variant,       // "grid" | "detail"
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls   = makeRootClass(variant, editable);
  const selectCls = `rt-select ${isGrid ? "rt-flat-select" : "dp-select"}`;
  const common    = getSelectCommon(variant, theme);

  const labelFor = (val) => {
    const opt = safeOptions.find((o) => o?.value === val);
    return opt?.name ?? (val ?? "");
  };

  const safeValue = Array.isArray(normalizedValue) ? normalizedValue : [];
  const labels = safeValue.map((v) => labelFor(v)).filter(Boolean);
  const firstLabel = labels[0] || "";
  const remainingCount = labels.length > 1 ? labels.length - 1 : 0;
  const fullLabelText = labels.join("; ");

  if (!editable) {
    const displayText = labels.join("; ");

    return (
      <Box className={rootCls} style={styleVars}>
        {displayText || <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        className={selectCls}
        multiple={true}
        value={safeValue}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          const arr = Array.isArray(selected) ? selected : [];
          if (arr.length === 0) return <Box className="rt-placeholder">No selection</Box>;

          const selectedLabels = arr.map((v) => labelFor(v)).filter(Boolean);
          const first = selectedLabels[0] || "";
          const extraCount = selectedLabels.length > 1 ? selectedLabels.length - 1 : 0;
          const tooltipText = selectedLabels.join("; ");

          return (
            <Tooltip title={tooltipText} placement="top" arrow>
              <Box className="rt-inline-center rt-multiselect-summary">
                <span className="rt-select-ellipsis">{first}</span>
                {extraCount > 0 && (
                  <Box component="span" className="rt-multiselect-count">
                    +{extraCount}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        }}
      >
        {safeOptions.map((opt, index) => (
          <MenuItem key={opt?.value ?? index} value={opt?.value} className="rt-menuitem">
            <FormControlLabel
              className="rt-fcl"
              control={
                <Checkbox
                  checked={safeValue.includes(opt?.value)}
                  className="rt-colored-checkbox"
                />
              }
              label={opt?.name}
            />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* ============================ PillSelect ========================== */

export function PillSelect({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  options,
  theme,
  variant,       // "grid" | "detail"
  allowNull = true,
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls   = makeRootClass(variant, editable);
  const selectCls = isGrid
    ? "rt-select rt-pill-select rt-flat-select"
    : "rt-select rt-pill-select dp-select";
  const common    = getSelectCommon(variant, theme);
  const labelStyles = getThemePath(theme, "labelOptions");
  const chipFixed = getChipFixedWidth(theme);

  const getOption = (val) => {
    const base = safeOptions.find((o) => o.value === val) || { value: val, name: val };
    const key  = base.name || base.value;
    const themed = labelStyles[key] || {};
    return {
      ...base,
      color: themed.colour,
      iconEl: themed.icon ? iconMap[themed.icon] : undefined,
    };
  };

  const renderStyledChip = (opt, isTrigger = false) => (
    <Chip
      key={opt.value}
      size="small"
      variant="outlined"
      label={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "100%", overflow: "hidden" }}>
          {opt.iconEl ? <span className="pill-chip-icon">{opt.iconEl}</span> : null}
          <span className="pill-chip-text" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.name}</span>
        </span>
      }
      className={`rt-pill-chip${opt.color ? " rt-pill-chip-inline" : ""}${isTrigger ? " rt-chip-trigger" : ""}`}
      style={opt.color ? { "--rt-chip": opt.color } : undefined}
      sx={{ width: chipFixed }}
    />
  );

  if (!editable) {
    if (multiple) {
      const vals = Array.isArray(normalizedValue) ? normalizedValue : [];
      const chips = vals.map((v) => renderStyledChip(getOption(v)));
      return <Box className={rootCls} style={styleVars}><Box className="rt-pills">{chips}</Box></Box>;
    }
    return <Box className={rootCls} style={styleVars}>{renderStyledChip(getOption(value))}</Box>;
  }

  const safeValue = multiple
    ? (Array.isArray(normalizedValue) ? normalizedValue : [])
    : (value ?? "");

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        className={selectCls}
        multiple={multiple}
        value={safeValue}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          if (!multiple) {
            if (!selected) return <Typography variant="body2" className="rt-empty" />;
            return renderStyledChip(getOption(selected), true);
          }
          if (!Array.isArray(selected) || selected.length === 0) {
            return <Box className="rt-placeholder">No selection</Box>;
          }
          const opts = selected.map((v) => getOption(v));
          const [first, ...rest] = opts;
          return (
            <Box className="rt-inline-center">
              {renderStyledChip(first, true)}
              {rest.length > 0 && (
                <Tooltip title={rest.map((o) => o.name).join("; ")}>
                  <Box component="span" className="rt-inline-plus rt-colored-plus" />
                </Tooltip>
              )}
            </Box>
          );
        }}
      >
        {!multiple && allowNull && <MenuItem value=""><em>None</em></MenuItem>}
        {safeOptions.map((opt) => {
          const themed = getOption(opt.value);
          return (
            <MenuItem key={opt.value} value={opt.value} className="rt-menuitem">
              <Box sx={{ display: "inline-flex", width: chipFixed }}>
                {renderStyledChip(themed)}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}

/* ============================ TreeSelect ========================== */

function TreeSelectRenderer({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  tree,
  theme,
  leafOnly,       // defaults from theme if undefined
  variant,        // "grid" | "detail"
}) {
  const data   = Array.isArray(tree) ? tree : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls = makeRootClass(variant, editable);

  const leafOnlyEff = leafOnly !== undefined ? leafOnly : getThemePath(theme, "selectTokens.tree.leafOnly");
  const padX = getPadX(theme, variant);

  const current = React.useMemo(() => {
    if (multiple) return Array.isArray(normalizedValue) ? normalizedValue : [];
    return normalizedValue || "";
  }, [normalizedValue, multiple]);

  const { valueToNode, isLeaf } = React.useMemo(() => {
    const map = new Map();
    const walk = (nodes) => (nodes || []).forEach((n) => {
      map.set(String(n.value), n);
      if (n.children?.length) walk(n.children);
    });
    walk(data);
    const leafCheck = (v) => {
      const n = map.get(String(v));
      return !!n && !(n.children && n.children.length);
    };
    return { valueToNode: map, isLeaf: leafCheck };
  }, [data]);

  const labelFor = React.useCallback(
    (v) => {
      const n = valueToNode.get(String(v));
      return n ? n.name : String(v ?? "");
    },
    [valueToNode]
  );

  const displayText = React.useMemo(() => {
    if (multiple) return (current || []).map(labelFor).filter(Boolean).join("; ");
    return current ? labelFor(current) : "";
  }, [current, multiple, labelFor]);

  const emitChange = (next) => onChange?.({ target: { value: next } });

  const toggleLeaf = (nodeValue) => {
    if (leafOnlyEff && !isLeaf(nodeValue)) return;
    if (multiple) {
      const set = new Set(current);
      set.has(nodeValue) ? set.delete(nodeValue) : set.add(nodeValue);
      emitChange(Array.from(set));
    } else {
      emitChange(nodeValue);
      handleClose();
    }
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleOpen  = (e) => { if (editable) setAnchorEl(e.currentTarget); };
  const handleClose = () => setAnchorEl(null);

  const defaultExpanded = React.useMemo(() => {
    const parentByVal = new Map();
    const indexParents = (nodes, parent = null) => {
      (nodes || []).forEach((n) => {
        const key = String(n.value);
        parentByVal.set(key, parent);
        if (n.children?.length) indexParents(n.children, key);
      });
    };
    indexParents(data);

    const collect = (val, acc) => {
      let p = parentByVal.get(String(val));
      while (p) { acc.push(p); p = parentByVal.get(p); }
    };

    const acc = [];
    if (multiple) (current || []).forEach((v) => collect(v, acc));
    else if (current) collect(current, acc);
    return Array.from(new Set(acc));
  }, [data, current, multiple]);

  const isChecked = (v) => multiple && Array.isArray(current) && current.includes(v);

  const renderNode = (node) => {
    const id = String(node.value);
    const nodeIsLeaf = !(node.children && node.children.length);

    const label = (
      <Box
        onClick={(e) => { if (nodeIsLeaf) { e.stopPropagation(); toggleLeaf(node.value); } }}
        className={`rt-treeitem-label ${nodeIsLeaf ? "rt-leaf" : "rt-branch"}`}
      >
        {nodeIsLeaf && multiple && (
          <Checkbox
            size="small"
            checked={isChecked(node.value)}
            onChange={(e) => { e.stopPropagation(); toggleLeaf(node.value); }}
            className="rt-treeitem-checkbox rt-colored-checkbox"
          />
        )}
        <Box className="rt-treeitem-text">
          <Typography variant="body2" className={`rt-treeitem-name ${nodeIsLeaf ? "" : "rt-treeitem-branch"}`}>
            {node.name}
          </Typography>
          {node.description ? (
            <Typography variant="caption" className="rt-treeitem-desc">
              {node.description}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );

    return (
      <TreeItem key={id} itemId={id} label={label} className="rt-treeitem">
        {(node.children || []).map(renderNode)}
      </TreeItem>
    );
  };

  if (!editable) {
    return (
      <Box className={rootCls} style={styleVars}>
        {displayText || <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  return (
    <Box className={rootCls} style={styleVars}>
      <OutlinedInput
        onClick={handleOpen}
        readOnly
        size={isGrid ? "small" : "medium"}
        fullWidth
        value=""
        className={`rt-select rt-tree-trigger rt-oi ${isGrid ? "" : "dp-tree-trigger"}`}
        sx={{
          "& .MuiInputBase-input": {
            paddingLeft: `${padX}px`,
            paddingRight: `${padX}px`,
          },
        }}
        startAdornment={
          <Box className="rt-oi-start">
            <Box className="rt-oi-start-text">
              {displayText || <span className="rt-placeholder">(select…)</span>}
            </Box>
          </Box>
        }
        endAdornment={
          <InputAdornment position="end" className="rt-oi-end">
            <IconButton edge="end" size="small" tabIndex={-1} disableRipple className="rt-oi-caret">
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: `rt-tree-popover ${isGrid ? "grid-enh-popover" : "dp-tree-popover"}` }}
      >
        <Box className="rt-tree">
          <SimpleTreeView
            defaultExpandedItems={defaultExpanded}
            aria-label="tree select"
            className="rt-treeview"
          >
            {data.map(renderNode)}
          </SimpleTreeView>
        </Box>
      </Popover>
    </Box>
  );
}

export const TreeSelect = (props) => <TreeSelectRenderer {...props} />;


/* ======================= EnhancedTreeSelect (new) ======================= */
/* ======================= EnhancedTreeSelect (modern UX) ======================= */
/* ======================= EnhancedTreeSelect (modern UX, FIXED) ======================= */
/* ======================= EnhancedTreeSelect (no filter; modern look) ======================= */
// EnhancedTreeSelectRenderer — contained popover, lineage rails, all-level backgrounds
// EnhancedTreeSelectRenderer — contained popover, lineage rails, all-level backgrounds
function EnhancedTreeSelectRenderer({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  tree,
  theme,
  leafOnly,               // defaults from theme if undefined
  variant,                // "grid" | "detail"
  env,                    // expect env.containerEl if you have one (detail)
}) {
  const data   = Array.isArray(tree) ? tree : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);          // CSS vars (brand, sizes, colors)
  const rootCls = makeRootClass(variant, editable);

  const leafOnlyEff = leafOnly !== undefined ? leafOnly : getThemePath(theme, "selectTokens.tree.leafOnly");

  // current value (for lineage; if multiple, use first to avoid visual noise)
  const current = React.useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(normalizedValue) ? normalizedValue : [];
      return arr[0] ?? "";
    }
    return normalizedValue || "";
  }, [normalizedValue, multiple]);

  // ---- index nodes + parents
  const { valueToNode, parentByVal, isLeaf } = React.useMemo(() => {
    const map = new Map();
    const parents = new Map();
    const walk = (nodes, parent = null) => {
      (nodes || []).forEach((n) => {
        const key = String(n.value);
        map.set(key, n);
        parents.set(key, parent);
        if (n.children?.length) walk(n.children, key);
      });
    };
    walk(data);
    const leafCheck = (v) => {
      const n = map.get(String(v));
      return !!n && !(n.children && n.children.length);
    };
    return { valueToNode: map, parentByVal: parents, isLeaf: leafCheck };
  }, [data]);

  const topAncestorOf = React.useCallback((val) => {
    let p = String(val);
    let prev = p;
    while (parentByVal.get(p)) {
      prev = parentByVal.get(p);
      p = prev;
    }
    return prev || null;
  }, [parentByVal]);

  // lineage ids for current selection (for rails)
  const lineage = React.useMemo(() => {
    if (!current) return {};
    const selected = String(current);
    const parent   = parentByVal.get(selected) || null;
    const ancestor = topAncestorOf(selected) || null;
    return { selected, parent, ancestor };
  }, [current, parentByVal, topAncestorOf]);

  const labelFor = React.useCallback(
    (v) => {
      const n = valueToNode.get(String(v));
      return n ? n.name : String(v ?? "");
    },
    [valueToNode]
  );

  const displayText = React.useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(normalizedValue) ? normalizedValue : [];
      return arr.map(labelFor).filter(Boolean).join("; ");
    }
    return current ? labelFor(current) : "";
  }, [current, multiple, normalizedValue, labelFor]);

  const emitChange = (next) => onChange?.({ target: { value: next } });

  const toggleLeaf = (nodeValue) => {
    if (leafOnlyEff && !isLeaf(nodeValue)) return;
    if (multiple) {
      const prev = Array.isArray(normalizedValue) ? normalizedValue : [];
      const set = new Set(prev);
      set.has(nodeValue) ? set.delete(nodeValue) : set.add(nodeValue);
      emitChange(Array.from(set));
    } else {
      emitChange(nodeValue);
      handleClose();
    }
  };

  // popover control (DP contained; Grid via portal)
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleOpen  = (e) => {
    if (!editable) return;
    e.stopPropagation(); // avoid DataGrid selection/focus churn
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  // expanded parents
  const defaultExpanded = React.useMemo(() => {
    const acc = [];
    const collect = (val) => {
      let p = parentByVal.get(String(val));
      while (p) { acc.push(p); p = parentByVal.get(p); }
    };
    if (multiple) {
      const arr = Array.isArray(normalizedValue) ? normalizedValue : [];
      arr.forEach(collect);
    } else if (current) collect(current);
    return Array.from(new Set(acc));
  }, [parentByVal, multiple, normalizedValue, current]);

  // block branch selection
  const selectedItems = React.useMemo(() => {
    if (!current) return [];
    return isLeaf(current) ? [String(current)] : [];
  }, [current, isLeaf]);

  const renderNode = (node, level = 0) => {
    const id = String(node.value);
    const nodeIsLeaf = !(node.children && node.children.length);

    let rail;
    if (lineage.selected && id === lineage.selected) rail = "selected";
    else if (lineage.parent && id === lineage.parent) rail = "parent";
    else if (lineage.ancestor && id === lineage.ancestor) rail = "ancestor";

    const clickable = nodeIsLeaf || !leafOnlyEff;

    const label = (
      <Box
        className="rt-enh-item"
        data-rail={rail || undefined}
        data-selected={lineage.selected && id === lineage.selected ? 1 : undefined}
        onMouseDown={(e) => { if (!nodeIsLeaf) e.preventDefault(); }}
        onClick={(e) => {
          if (!clickable) return;
          if (nodeIsLeaf) { e.stopPropagation(); toggleLeaf(node.value); }
        }}
      >
        <Box className="rt-enh-spacer" />
        <Box className="rt-enh-text">
          <Typography variant="body2" className={`rt-treeitem-name ${nodeIsLeaf ? "" : "rt-treeitem-branch"}`}>
            {node.name}
          </Typography>
          {node.description ? (
            <Typography variant="caption" className="rt-treeitem-desc">
              {node.description}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );

    return (
      <TreeItem
        key={id}
        itemId={id}
        label={label}
        className={`rt-treeitem rt-enh-node rt-level-${level}${nodeIsLeaf ? "" : " rt-branch-node"}`}
      >
        {(node.children || []).map((child) => renderNode(child, level + 1))}
      </TreeItem>
    );
  };

  if (!editable) {
    return (
      <Box className={rootCls} style={styleVars}>
        {displayText || <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  // Always include dp-tree-popover for the “blue” DP look.
  // Add grid-enh-popover only in Grid for height/offset tweaks.
  const paperClass = isGrid
    ? "rt-enhanced-scope rt-tree-popover dp-tree-popover grid-enh-popover"
    : "rt-enhanced-scope rt-tree-popover dp-tree-popover";

  return (
    <Box className={`${rootCls} rt-enhanced-scope`} style={styleVars} onMouseDown={(e) => e.stopPropagation()}>
      <OutlinedInput
        onClick={handleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        readOnly
        size={isGrid ? "small" : "medium"}
        fullWidth
        value=""
        className={`rt-select rt-tree-trigger rt-oi ${isGrid ? "" : "dp-tree-trigger"}`}
        startAdornment={
          <Box className="rt-oi-start">
            <Box className="rt-oi-start-text">
              {displayText || <span className="rt-placeholder">(select…)</span>}
            </Box>
          </Box>
        }
        endAdornment={
          <InputAdornment position="end" className="rt-oi-end">
            <IconButton edge="end" size="small" tabIndex={-1} disableRipple className="rt-oi-caret" onMouseDown={(e) => e.stopPropagation()}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        // Grid: portal to body; DP: contain inside detail panel
        disablePortal={isGrid ? false : true}
        container={isGrid ? undefined : (env?.containerEl || undefined)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Use PaperProps (not slotProps) so classes apply in all MUI versions
        PaperProps={{
          className: paperClass,
          style: styleVars, // pass CSS vars into the portal (for brand/blue tokens)
        }}
      >
        <Box className="rt-tree rt-tree--enhanced" onMouseDown={(e) => e.stopPropagation()}>
          <SimpleTreeView
            defaultExpandedItems={defaultExpanded}
            selectedItems={selectedItems}
            aria-label="enhanced tree select"
            className="rt-treeview rt-treeview--enh rt-treeview--modern"
          >
            {data.map((n) => renderNode(n, 0))}
          </SimpleTreeView>
        </Box>
      </Popover>
    </Box>
  );
}



export const EnhancedTreeSelect = (props) => <EnhancedTreeSelectRenderer {...props} />;







/* ============================ CountrySelect ======================== */

export function CountrySelect({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  options,
  theme,
  variant,       // "grid" | "detail"
  allowNull = true,
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls   = makeRootClass(variant, editable);
  const selectCls = `rt-select ${isGrid ? "rt-flat-select" : "dp-select"}`;
  const common    = getSelectCommon(variant, theme);

  const flagUrl = (iso) => {
    if (!iso) return null;
    const code = String(iso).toLowerCase();
    return `https://flagcdn.com/w20/${code}.png`;
  };

  const findOpt = (v) => safeOptions.find((o) => o.value === v) || null;

  if (!editable) {
    if (multiple) {
      const labels = (Array.isArray(normalizedValue) ? normalizedValue : [])
        .map((v) => findOpt(v)?.name || v)
        .filter(Boolean)
        .join("; ");
      return <Box className={rootCls} style={styleVars}>{labels || <Typography variant="body2" className="rt-empty" />}</Box>;
    }
    const opt = findOpt(value);
    return (
      <Box className={rootCls} style={styleVars}>
        {opt ? (
          <span className="rt-inline-center">
            {opt.value ? (
              <img
                src={flagUrl(opt.value)}
                alt={`${opt.name} flag`}
                className="rt-flag"
                style={{ marginRight: 6 }}
              />
            ) : null}
            {opt.name}
          </span>
        ) : <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  const safeValue = multiple
    ? (Array.isArray(normalizedValue) ? normalizedValue : [])
    : (value ?? "");

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        className={selectCls}
        multiple={multiple}
        value={safeValue}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          if (!multiple) {
            const sel = findOpt(selected);
            if (!sel) return <Typography variant="body2" className="rt-empty" />;
            return (
              <span className="rt-inline-center">
                {sel.value ? (
                  <img
                    src={flagUrl(sel.value)}
                    alt={`${sel.name} flag`}
                    className="rt-flag"
                    style={{ marginRight: 6 }}
                  />
                ) : null}
                {sel.name}
              </span>
            );
          }
          const arr = Array.isArray(selected) ? selected : [];
          if (arr.length === 0) return <Box className="rt-placeholder">No selection</Box>;
          const labels = arr.map((v) => findOpt(v)?.name || v);
          const first = findOpt(arr[0]);
          return (
            <Box className="rt-inline-center">
              {first ? (
                <>
                  {first.value ? (
                    <img
                      src={flagUrl(first.value)}
                      alt={`${first.name} flag`}
                      className="rt-flag"
                      style={{ marginRight: 6 }}
                    />
                  ) : null}
                  <span>{first.name}</span>
                </>
              ) : arr[0]}
              {labels.length > 1 && (
                <Tooltip title={labels.slice(1).join("; ")}>
                  <Box component="span" className="rt-inline-plus rt-colored-plus" />
                </Tooltip>
              )}
            </Box>
          );
        }}
      >
        {!multiple && allowNull && <MenuItem value=""><em>None</em></MenuItem>}
        {safeOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} className="rt-menuitem">
            <span className="rt-inline-center">
              {opt.value ? (
                <img
                  src={flagUrl(opt.value)}
                  alt={`${opt.name} flag`}
                  className="rt-flag"
                  style={{ marginRight: 8 }}
                />
              ) : null}
              {opt.name}
            </span>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* =========================== LanguageSelect ======================== */

export function LanguageSelect({
  normalizedValue,
  multiple,
  editable,
  value,
  onChange,
  options,
  theme,
  variant,       // "grid" | "detail"
  allowNull = true,
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const isGrid = variant === "grid";
  const styleVars = buildStyleVars(theme);
  const rootCls   = makeRootClass(variant, editable);
  const selectCls = `rt-select ${isGrid ? "rt-flat-select" : "dp-select"}`;
  const common    = getSelectCommon(variant, theme);

  const langFlagKey = (lang) => {
    if (!lang) return null;
    const lc = String(lang).toLowerCase();
    if (lc === "en") return "gb-eng";
    return lc;
  };

  const flagUrl = (key) => (key ? `https://flagcdn.com/w20/${key}.png` : null);
  const findOpt = (v) => safeOptions.find((o) => o.value === v) || null;

  if (!editable) {
    if (multiple) {
      const vals = Array.isArray(normalizedValue) ? normalizedValue : [];
      const labels = vals.map((v) => findOpt(v)?.name || String(v ?? "")).filter(Boolean).join("; ");
      return <Box className={rootCls} style={styleVars}>{labels || <Typography variant="body2" className="rt-empty" />}</Box>;
    }
    const opt = findOpt(value);
    if (!opt) return <Box className={rootCls} style={styleVars}><Typography variant="body2" className="rt-empty" /></Box>;
    const key = langFlagKey(opt.value);
    return (
      <Box className={rootCls} style={styleVars}>
        <span className="rt-inline-center">
          {key ? (
            <img
              src={flagUrl(key)}
              alt={`${opt.name} flag`}
              className="language-flag rt-flag"
              style={{ marginRight: 6 }}
            />
          ) : null}
          {opt.name}
        </span>
      </Box>
    );
  }

  const safeValue = multiple
    ? (Array.isArray(normalizedValue) ? normalizedValue : [])
    : (value ?? "");

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        className={selectCls}
        multiple={multiple}
        value={safeValue}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          if (!multiple) {
            const sel = findOpt(selected);
            if (!sel) {
              const raw = selected ?? value ?? "";
              return raw ? <span>{String(raw)}</span> : <Typography variant="body2" className="rt-empty" />;
            }
            const key = langFlagKey(sel.value);
            return (
              <span className="rt-inline-center">
                {key ? (
                  <img
                    src={flagUrl(key)}
                    alt={`${sel.name} flag`}
                    className="language-flag rt-flag"
                    style={{ marginRight: 6 }}
                  />
                ) : null}
                {sel.name}
              </span>
            );
          }
          const arr = Array.isArray(selected) ? selected : [];
          if (arr.length === 0) return <Box className="rt-placeholder">No selection</Box>;
          const labels = arr.map((v) => findOpt(v)?.name || String(v ?? ""));
          const firstVal = arr[0];
          const firstOpt = findOpt(firstVal);
          if (!firstOpt) return <span>{String(firstVal ?? "")}</span>;
          const key = langFlagKey(firstOpt.value);
          return (
            <Box className="rt-inline-center">
              {key ? (
                <img
                  src={flagUrl(key)}
                  alt={`${firstOpt.name} flag`}
                  className="language-flag rt-flag"
                  style={{ marginRight: 6 }}
                />
              ) : null}
              <span>{firstOpt.name}</span>
              {labels.length > 1 && (
                <Tooltip title={labels.slice(1).join("; ")}>
                  <Box component="span" className="rt-inline-plus rt-colored-plus" />
                </Tooltip>
              )}
            </Box>
          );
        }}
      >
        {!multiple && allowNull && <MenuItem value=""><em>None</em></MenuItem>}
        {safeOptions.map((opt) => {
          const key = langFlagKey(opt.value);
          return (
            <MenuItem key={opt.value} value={opt.value} className="rt-menuitem">
              <span className="rt-inline-center">
                {key ? (
                  <img
                    src={flagUrl(key)}
                    alt={`${opt.name} flag`}
                    className="language-flag rt-flag"
                    style={{ marginRight: 8 }}
                  />
                ) : null}
                {opt.name}
              </span>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}

/* ============================ BooleanSelect ============================ */

export function BooleanSelect({
  normalizedValue,
  multiple,          // ignored (always single-select)
  editable,
  value,
  onChange,
  options,
  theme,
  variant,           // "grid" | "detail"
  allowNull = true,
}) {
  const styleVars = buildStyleVars(theme);
  const rootCls = makeRootClass(variant, editable);
  const common  = getSelectCommon(variant, theme);

  const safeOptions = Array.isArray(options) ? options : [];

  // normalizedValue is the selected value (UI-safe) — for BooleanSelect this should be: "", true, or false
  const current =
    normalizedValue === undefined || normalizedValue === null ? "" : normalizedValue;

  const Tick = <CheckIcon fontSize="small" />;
  const Cross = <CloseIcon fontSize="small" />;

  const renderIconOnly = (val) => (val === true ? Tick : val === false ? Cross : null);

  if (!editable) {
    return (
      <Box className={rootCls} style={styleVars}>
        {renderIconOnly(current) || <Typography variant="body2" className="rt-empty" />}
      </Box>
    );
  }

  return (
    <Box className={rootCls} style={styleVars}>
      <Select
        value={current}
        onChange={onChange}
        displayEmpty
        {...common}
        renderValue={(selected) => {
          if (selected === "" || selected === null || selected === undefined) {
            return allowNull ? <Typography variant="body2" className="rt-empty" /> : renderIconOnly(false);
          }
          return <Box className="rt-inline-center">{renderIconOnly(selected)}</Box>;
        }}
      >
        {allowNull && <MenuItem value="">{/* blank option */}</MenuItem>}

        {safeOptions.map((opt) => (
          <MenuItem
            key={String(opt?.value)}
            value={opt?.value}
            className="rt-menuitem"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ListItemIcon>{renderIconOnly(opt?.value)}</ListItemIcon>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* ============================ AIAssignedBadge (readonly) ============================ */

export function AIAssignedBadge({ normalizedValue, theme, variant }) {
  const styleVars = buildStyleVars(theme);
  const rootCls = makeRootClass(variant, false);
  const aiColour = getThemePath(theme, "aiColour") || "#B565D8";

  const isAssigned = normalizedValue === true;

  return (
    <Box className={rootCls} style={styleVars}>
      {isAssigned ? (
        <Chip
          icon={<AutoAwesomeRoundedIcon />}
          label="AI"
          size="small"
          sx={{
            bgcolor: aiColour,
            color: "#fff",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />
      ) : (
        <Typography variant="body2" className="rt-empty" />
      )}
    </Box>
  );
}

/* ============================ registry ============================ */

export const renderTemplates = {
  DefaultSelect,
  MultiSelect,
  PillSelect,
  TreeSelect,
  EnhancedTreeSelect,
  CountrySelect,
  LanguageSelect,
  BooleanSelect,
  AIAssignedBadge,
};
